import React from 'react';
import { cn } from '../../../utils/cn';
import type { CockpitSummary } from '../../cashflow/types';
import { AGING_META, AGING_ORDER, inr, RECON } from '../display';
import { CashflowFilter, FoldTiles, ReconClass } from '../types';

/**
 * The Executive Brief band (spec §3), compact layout:
 *   eyebrow · headline ("They owe us") + side stats · movement row (plain-
 *   language cards: Disputed / Chase / Leak / Verify collection) · aging
 *   ribbon · deep-review strip.
 * Every number click-filters the list. Depth-fed parts (ribbon, past-90, top
 * exposure, strip) light up when /recon/master lands.
 */
interface Props {
	tiles: FoldTiles;
	summary: CockpitSummary | null;
	activeFilter: CashflowFilter | null;
	onFilter: (f: CashflowFilter | null) => void;
}

const n = (v: string | number | null | undefined): number => Number(v) || 0;

const shortName = (s?: string): string =>
	(s || '').replace(/\s+(PRIVATE|LIMITED|LTD|PVT|INDIA|SERVICES)\b.*/i, '').trim();

export const ExecBrief: React.FC<Props> = ({ tiles, summary, activeFilter, onFilter }) => {
	const eb = summary?.exec_brief;
	const oweUs = n(tiles.rc_disputed_amt) + n(tiles.rc_open_amt) + n(tiles.rc_leak_amt);
	const oweUsN = n(tiles.rc_disputed) + n(tiles.rc_open) + n(tiles.rc_leak);
	const agingTotal = eb ? AGING_ORDER.reduce((s, b) => s + n(eb.aging[b]?.inr), 0) : 0;

	const toggle = (f: CashflowFilter) => {
		const same =
			activeFilter &&
			activeFilter.recon === f.recon &&
			activeFilter.aging === f.aging &&
			activeFilter.audit === f.audit;
		onFilter(same ? null : f);
	};

	/** Plain-language movement cards (user-named): Disputed / Chase / Leak / Verify collection. */
	const movement: Array<{ rc: ReconClass; title: string; value: string; sub: string }> = [
		{ rc: 'DISPUTED', title: 'Disputed', value: inr(n(tiles.rc_disputed_amt)), sub: `${n(tiles.rc_disputed)} invoices · resolve` },
		{ rc: 'OPEN', title: 'Chase', value: inr(n(tiles.rc_open_amt)), sub: `${n(tiles.rc_open)} open · awaiting payment` },
		{ rc: 'LEAK', title: 'Leak', value: inr(n(tiles.rc_leak_amt)), sub: `${n(tiles.rc_leak)} · client paid, Zoho unpaid` },
		{ rc: 'PAID_UNPROVEN', title: 'Verify collection', value: String(n(tiles.rc_paid_unproven)), sub: 'Zoho paid · no email trail' },
	];

	return (
		<div className='overflow-hidden rounded-lg border border-slate-200 bg-white'>
			{/* eyebrow */}
			<div className='flex items-baseline justify-between px-6 pt-4'>
				<div className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>Executive brief</div>
				<div className='flex items-baseline gap-4 text-[11px] text-slate-400'>
					<span className='text-emerald-600'>✓ {String(n(tiles.rc_reconciled))} reconciled</span>
					{summary?.generated_at && (
						<span>{new Date(summary.generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
					)}
				</div>
			</div>

			{/* headline + side stats */}
			<div className='flex flex-wrap items-end justify-between gap-x-6 gap-y-2 px-6 pb-4 pt-1.5'>
				<button onClick={() => onFilter(null)} className='text-left'>
					<div className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>They owe us</div>
					<div className='flex items-baseline gap-2'>
						<span className='text-2xl font-bold leading-8 tabular-nums text-[#191c1e]'>{inr(oweUs)}</span>
						<span className='text-xs text-slate-400'>across {oweUsN} invoices</span>
					</div>
				</button>
				<div className='flex items-end gap-6'>
					<button
						onClick={() => eb && toggle({ aging: '90+' })}
						disabled={!eb}
						className={cn('text-right', activeFilter?.aging === '90+' && 'rounded ring-2 ring-red-200')}
					>
						<div className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>Past 90 days</div>
						<div className='text-base font-bold tabular-nums text-amber-600'>{eb ? inr(eb.aging['90+']?.inr) : '—'}</div>
					</button>
					<div className='text-right'>
						<div className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>Top exposure</div>
						<div className='max-w-[220px] truncate text-sm font-bold text-slate-800'>
							{shortName(eb?.top_customers?.[0]?.name) || '—'}
							{eb?.top_customers?.[1] ? <span className='font-medium text-slate-400'> & {shortName(eb.top_customers[1].name)}</span> : null}
						</div>
					</div>
				</div>
			</div>

			{/* movement row — plain-language cards */}
			<div className='grid grid-cols-2 divide-slate-100 border-t border-slate-100 max-sm:divide-y sm:grid-cols-4 sm:divide-x'>
				{movement.map(m => {
					const cfg = RECON[m.rc];
					const active = activeFilter?.recon === m.rc;
					return (
						<button
							key={m.rc}
							onClick={() => toggle({ recon: m.rc })}
							className={cn('group relative px-4 py-2.5 text-left transition-colors hover:bg-slate-50', active && 'ring-2 ring-inset ring-slate-300')}
						>
							<span className={cn('absolute left-0 top-2.5 h-7 w-1 rounded-r', cfg.bar, active ? 'opacity-100' : 'opacity-50 group-hover:opacity-100')} />
							<div className='pl-2'>
								<div className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>{m.title}</div>
								<div className={cn('text-xl font-bold leading-7 tabular-nums', cfg.text)}>{m.value}</div>
								<div className='text-[11px] leading-4 text-slate-400'>{m.sub}</div>
							</div>
						</button>
					);
				})}
			</div>

			{/* aging ribbon (depth-fed) */}
			<div className='border-t border-slate-100 px-6 py-3'>
				{eb && agingTotal > 0 ? (
					<div className='flex flex-wrap items-center gap-x-4 gap-y-1.5'>
						<div className='flex h-2 min-w-[180px] flex-1 overflow-hidden rounded-full'>
							{AGING_ORDER.map(b => (
								<button
									key={b}
									onClick={() => toggle({ aging: b })}
									title={`${AGING_META[b].label} · ${inr(eb.aging[b]?.inr)}`}
									className={cn(AGING_META[b].seg, 'transition-opacity hover:opacity-80', activeFilter?.aging && activeFilter.aging !== b && 'opacity-40')}
									style={{ width: `${Math.max(1, (n(eb.aging[b]?.inr) / agingTotal) * 100)}%` }}
								/>
							))}
						</div>
						<div className='flex flex-wrap gap-x-3.5 gap-y-1'>
							{AGING_ORDER.map(b => (
								<button key={b} onClick={() => toggle({ aging: b })} className={cn('text-[11px] tabular-nums', AGING_META[b].text, activeFilter?.aging === b ? 'font-extrabold underline' : 'font-medium hover:underline')}>
									● {AGING_META[b].label} {inr(eb.aging[b]?.inr)}
								</button>
							))}
						</div>
					</div>
				) : (
					<div className='text-[11px] text-slate-300'>aging — loading depth…</div>
				)}
			</div>

			{/* deep-review strip (depth-fed; hidden when nothing audited) */}
			{eb?.forensic && eb.forensic.audited > 0 && (
				<div className='flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/50 px-6 py-2 text-xs text-slate-500'>
					<span className='font-bold text-slate-600'>◆ Deep review</span>
					<span><b className='tabular-nums text-slate-700'>{eb.forensic.audited}</b> read end-to-end</span>
					<button onClick={() => toggle({ audit: 'audit_disagreement' })} className={cn('hover:underline', activeFilter?.audit === 'audit_disagreement' && 'font-bold underline')}>
						<b className='tabular-nums text-red-600'>{eb.forensic.audit_disagreements}</b> audit corrects dashboard
					</button>
					<span><b className='tabular-nums text-red-600'>{eb.forensic.with_high_tactics}</b> aggressive pushback</span>
					<span><b className='tabular-nums text-slate-700'>{eb.forensic.hearsay_claims}</b> unproven claims</span>
					<button onClick={() => toggle({ audit: 'audit_misclassified' })} className={cn('hover:underline', activeFilter?.audit === 'audit_misclassified' && 'font-bold underline')}>
						<b className='tabular-nums text-slate-700'>{eb.forensic.misclassified}</b> misclassified
					</button>
				</div>
			)}
		</div>
	);
};
